/**
 * DcmTeamActivityPanel — org-wide team activity feed (TRUS-538, phase 2).
 *
 * "While they are still getting acclimated to the platform, they want to have
 * the ability to monitor what their team is doing." That is this panel: every
 * case note, task, case/member change, workflow step and logged activity across
 * the organization, newest first, filterable down to a single case manager.
 *
 * Deliberately a TIMELINE, not a table. These rows have no shared columns —
 * a note has a body, a task has a deadline, an audit row has a field list — so
 * a table would be mostly empty cells. What they share is when, who, and about
 * whom, which is exactly what a timeline leads with.
 *
 * The case-manager filter reuses the overview's `by_dcm` list rather than
 * fetching a separate roster: the people carrying cases are the ones whose
 * activity a DCM dashboard is about, and it saves a round trip.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckSquare,
  ClipboardList,
  FileText,
  Loader2,
  RefreshCw,
  UserCog,
  Workflow,
} from 'lucide-react';

import {
  dcmDashboardAPI,
  DcmActivityFilters,
  DcmActivityItem,
  DcmCaseloadRow,
  DcmFeedKind,
} from '@/services/api/dcmDashboard';

const PAGE_SIZE = 50;

const WINDOW_OPTIONS = [
  { value: 1, label: 'Last 24 hours' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

/**
 * Per-kind presentation. Icons carry a text label beside them everywhere, so
 * the icon is a scanning aid and never the only way to tell rows apart.
 */
const KIND_META: Record<DcmFeedKind, { label: string; icon: React.ReactNode }> = {
  note: { label: 'Case note', icon: <FileText className="h-3.5 w-3.5" /> },
  task: { label: 'Task', icon: <CheckSquare className="h-3.5 w-3.5" /> },
  case_update: { label: 'Case change', icon: <ClipboardList className="h-3.5 w-3.5" /> },
  member_update: { label: 'Member change', icon: <UserCog className="h-3.5 w-3.5" /> },
  workflow: { label: 'Workflow', icon: <Workflow className="h-3.5 w-3.5" /> },
  activity: { label: 'Activity', icon: <Activity className="h-3.5 w-3.5" /> },
};

const fmtWhen = (iso: string | null): string => {
  if (!iso) return 'Unknown time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown time';

  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return d.toLocaleDateString();
};

const fmtExact = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const FeedRow: React.FC<{ item: DcmActivityItem }> = ({ item }) => {
  const meta = KIND_META[item.kind] || KIND_META.activity;
  const failed = Boolean(item.meta?.error);

  return (
    <li
      className={`flex gap-3 border-b border-gray-100 py-3 last:border-0 dark:border-gray-700/50 ${
        failed ? 'opacity-70' : ''
      }`}
    >
      <div className="mt-0.5 shrink-0 text-gray-400" aria-hidden>
        {failed ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> : meta.icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title || meta.label}</span>

          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {meta.label}
          </span>

          {item.meta?.is_reassignment && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
              Reassigned
            </span>
          )}
        </div>

        {item.body && (
          <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400">{item.body}</p>
        )}

        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {item.actor_name || 'Unknown staff'}
          {item.member_name && (
            <>
              {' · '}
              <span className="text-gray-600 dark:text-gray-300">{item.member_name}</span>
            </>
          )}
          {' · '}
          <span title={fmtExact(item.occurred_at)}>{fmtWhen(item.occurred_at)}</span>
        </p>
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export const DcmTeamActivityPanel: React.FC = () => {
  const [items, setItems] = useState<DcmActivityItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [days, setDays] = useState(7);
  const [actorId, setActorId] = useState<number | undefined>();
  const [origin, setOrigin] = useState<'note' | 'system' | undefined>();
  const [staff, setStaff] = useState<DcmCaseloadRow[]>([]);

  const filters: DcmActivityFilters = useMemo(
    () => ({ days, actor_id: actorId, origin }),
    [days, actorId, origin],
  );

  // The case-manager picker. A failure here must not block the feed — the feed
  // is the feature; the filter is a convenience.
  useEffect(() => {
    let cancelled = false;
    dcmDashboardAPI
      .getOverview()
      .then((o) => {
        if (!cancelled) setStaff((o.by_dcm || []).filter((r) => r.user_id !== null));
      })
      .catch(() => {
        if (!cancelled) setStaff([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dcmDashboardAPI
      .getActivity(filters, PAGE_SIZE, 0)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items || []);
        setHasMore(Boolean(page.has_more));
        setOffset(page.items?.length || 0);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load team activity');
        setItems([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => load(), [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await dcmDashboardAPI.getActivity(filters, PAGE_SIZE, offset);
      setItems((prev) => [...prev, ...(page.items || [])]);
      setHasMore(Boolean(page.has_more));
      setOffset((prev) => prev + (page.items?.length || 0));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const selectClass =
    'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

  return (
    <div className="space-y-4">
      <header>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <Activity className="h-5 w-5 text-blue-600" />
          Team Activity
        </h2>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          Case notes, tasks and record changes across the organization, newest first.
        </p>
      </header>

      {/* Filters in one row above the feed. */}
      <div className="flex flex-wrap items-center gap-2" data-export-ignore="true">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={selectClass}>
          {WINDOW_OPTIONS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </select>

        <select
          value={actorId?.toString() || '__all__'}
          onChange={(e) => setActorId(e.target.value === '__all__' ? undefined : Number(e.target.value))}
          className={`${selectClass} min-w-[180px]`}
        >
          <option value="__all__">All staff</option>
          {staff.map((s) => (
            <option key={s.user_id ?? 'none'} value={String(s.user_id)}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          value={origin || '__all__'}
          onChange={(e) =>
            setOrigin(e.target.value === '__all__' ? undefined : (e.target.value as 'note' | 'system'))
          }
          className={selectClass}
        >
          <option value="__all__">Everything</option>
          <option value="note">Notes &amp; tasks</option>
          <option value="system">Record changes</option>
        </select>

        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          {loading ? 'Loading…' : `${items.length} event${items.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading team activity…
          </div>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            Nothing recorded in this window. Try a longer range or clear the filters.
          </p>
        ) : (
          <>
            <ul>
              {items.map((item) => (
                <FeedRow key={item.id} item={item} />
              ))}
            </ul>
            {hasMore && (
              <div className="py-3 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default DcmTeamActivityPanel;
